import {
  Create,
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  NumberInput,
  required,
  useEditContext,
  useDataProvider,
} from "react-admin";
import { Box, Typography, Divider } from "@mui/material";
import { useWatch } from "react-hook-form";
import { ResourceName } from "@/types";
import { ArticleBodyInput } from "./ArticleBodyInput";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { toAbsoluteImageUrls } from "./imageUrls";
import { sanitizeArticleHtml } from "@/utils/sanitizeHtml";
import { SectionCard } from "@/components/common/SectionCard";
import { PageHeader } from "@/components/common/PageHeader";
import { HelpOutline } from "@mui/icons-material";

const statusChoices = [
  { id: "draft", name: "Draft" },
  { id: "published", name: "Published" },
];

/** Live preview of the article as it will render in the Help Center. */
const ArticlePreview = () => {
  const title = useWatch<{ title?: string }>({ name: "title" });
  const body = useWatch<{ body_html?: string }>({ name: "body_html" });
  return (
    <SectionCard title="Preview">
      <Box sx={{ minHeight: 320 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom color={title ? "text.primary" : "text.disabled"}>
          {title || "Untitled article"}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {body ? (
          <Box
            sx={{ "& img": { maxWidth: "100%" }, "& h2,& h3": { mt: 2 } }}
            dangerouslySetInnerHTML={{
              __html: sanitizeArticleHtml(toAbsoluteImageUrls(body, getPartnerAwareApiUrl())),
            }}
          />
        ) : (
          <Typography variant="body2" color="text.disabled">
            Nothing to preview yet — start writing in the body.
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
};

/**
 * Shared article form (Feature 4317 / N3). Two-column, card-grouped layout
 * matching the app's SectionCard aesthetic: a wide Content card (title, slug,
 * rich-text body) beside a narrow Publishing card (category, status). Body is
 * sanitized HTML via ra-input-rich-text — no markdown.
 */
const HelpArticleFormBody = () => {
  const dataProvider = useDataProvider();
  const notifyError = useRichApiErrorNotify();

  // Inline quick-add: typing a new category name and choosing "Add new category"
  // creates it via POST /help-categories (SuperAdmin only) and selects it, with
  // no separate management screen. The API derives the slug from the name.
  const handleCreateCategory = async (filterValue?: string) => {
    const name = (filterValue ?? "").trim();
    if (!name) return undefined;
    try {
      const { data } = await dataProvider.create(ResourceName.HELP_CATEGORIES, { data: { name } });
      return data;
    } catch (error) {
      notifyError(error);
      return undefined;
    }
  };

  return (
    <SimpleForm sx={{ maxWidth: 1700 }}>
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          width: "100%",
          // Even 50/50 split: roomy form on the left, equally wide preview on the
          // right so screenshots/images render at a realistic width.
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" },
          alignItems: "start",
        }}
      >
        {/* Left: single Content card — title, slug, body, then a compact meta row */}
        <SectionCard title="Content">
          <TextInput source="title" validate={required()} fullWidth />
          <TextInput
            source="slug"
            validate={required()}
            fullWidth
            helperText="URL-safe identifier used by contextual-help deep links, e.g. getting-started"
          />
          <ArticleBodyInput />

          {/* Publishing metadata — three small fields in a row below the body */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              mt: 1,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              alignItems: "start",
              // Normalize each input's top margin so Category/Status/Order align
              // on the same baseline (SelectInput otherwise sits slightly lower).
              "& .MuiFormControl-root": { marginTop: 0 },
            }}
          >
            <ReferenceInput source="category_id" reference={ResourceName.HELP_CATEGORIES}>
              <AutocompleteInput
                optionText="name"
                label="Category"
                fullWidth
                onCreate={handleCreateCategory}
                createItemLabel='Add new category: "%{item}"'
              />
            </ReferenceInput>
            <SelectInput source="status" choices={statusChoices} defaultValue="draft" validate={required()} fullWidth />
            <NumberInput
              source="sort_order"
              label="Order"
              min={1}
              fullWidth
              helperText="Lower shows first; blank = end"
            />
          </Box>
        </SectionCard>

        {/* Right: live preview, sticky so it stays in view while editing */}
        <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>
          <ArticlePreview />
        </Box>
      </Box>
    </SimpleForm>
  );
};

export const HelpArticleCreate = () => (
  <>
    <PageHeader
      customTitle="New Article"
      customSubtitle="Author a Knowledge Base article. Save as a draft, or publish to make it visible in the Help Center."
      hideSectionTitle
      headerIcon={<HelpOutline sx={{ fontSize: 28, color: "primary.main" }} />}
    />
    <Create redirect="list" title=" " component={Box}>
      <HelpArticleFormBody />
    </Create>
  </>
);

const EditHeader = () => {
  const { record } = useEditContext<{ title?: string }>();
  return (
    <PageHeader
      customTitle={record?.title || "Edit Article"}
      customSubtitle="Edit content, change the category, or publish/unpublish."
      hideSectionTitle
      headerIcon={<HelpOutline sx={{ fontSize: 28, color: "primary.main" }} />}
    />
  );
};

export const HelpArticleEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic" title=" " component={Box}>
    <EditHeader />
    <HelpArticleFormBody />
  </Edit>
);
